import type React from "react"

import type { AgentInfo } from "@/agents/info"
import type { PerMessageReviewConfig } from "@/human-feedback/per-message"
import type { PerSegmentReviewConfig } from "@/human-feedback/per-segment"
import type { CompareBy } from "@/metrics/compare-by"
import type { Annotation } from "@/types"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  AnnotationRenderContext,
  WordHighlight,
} from "@/components/transcript/word-highlight"
import type { SimulatorType } from "@/types/api/SimulatorType"
import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { MetricsOnlyLaunchConfig } from "@/types/LaunchConfig"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import type { Message } from "@/types/Transcript"
import { renderQuestionWithAttributes } from "@/utils/backend-mimics/renderQuestionWithAttributes"
import type { AttributeContext } from "@/utils/backend-mimics/replace_with_attributes"
import type { SimulationWithContext } from "@/utils/backend-mimics/SimulationWithContext"

import {
  renderDefaultAggregateAlertForm,
  renderDefaultFilterSelection,
  renderDefaultReadOnlyMetadata,
  renderDefaultSingleOutputAlertForm,
  renderEmptyEditorMetadata,
  renderEmptyMetadataField,
  renderEmptyMetricType,
} from "../rendering/editorRenderers"
import {
  evaluateTranscriptDefault,
  getDefaultKeyboardShortcutHints,
  renderAssignmentsTableAIResultCell as renderAssignmentsTableAIResultCellDefault,
  renderComparedInDepthRunReviewDefault,
  renderDeepDiveDefault,
  renderDefaultAssignmentsTableReviewCell,
  renderDefaultAssignmentsTableSubRows,
  renderInDepthRunReviewDefault,
} from "../rendering/reviewAndTesting"
import {
  getDefaultDisplayValueString,
  getDefaultHumanReviewDisplayValue,
  renderDefaultBadge,
  renderDefaultSimulationCard,
  renderStatusBasedBadge,
  renderValueOnlyTableCell as renderValueOnlyTableCellDefault,
} from "../rendering/statusRenderers"
import type { AudioRegionReviewConfig } from "../types/audioRegionReviewConfig"
import type { MetricReviewTranscriptContext } from "../types/metricReviewTranscriptContext"
import type { TestResult } from "../types/testResult"
import {
  getAverageValue,
  getMaxValue,
  getMedianValue,
  getMinValue,
  getNumericValuesForAggregation,
  getP95Value,
  renderGroupedSimulationCard,
  type GroupedSimulationCardOptions,
} from "./metricRecordAggregation"
import { MetricRecordCore } from "./MetricRecordCore"
import { getCompletedMetricOutputs } from "./metricRecordOutputBehavior"

export {
  supportsAgentJudgeTools,
  supportsAudioFilter,
  supportsModelSelection,
  supportsTraceContext,
  supportsTranscriptFilter,
} from "./metricRecordCapabilities"

interface MetricEditorContext {
  readOnly?: boolean
  selectedAgentIds?: string[]
}
type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

/**
 * Result of getExplanationTable: columns to show and a function to get cell values per run row.
 * Used when metricIdFilter is set to render explanation columns in the runs table.
 * Optional width/minWidth/maxWidth (CSS values) let the metric control column sizing.
 * getRowValues must return only string values (one per column key).
 */
export interface DeepDiveContext {
  simulationOutputId?: string
  annotationsForMetric?: Annotation[]
}

interface ExplanationTableResult {
  columns: {
    key: string
    title: string
    width?: string
    minWidth?: string
    maxWidth?: string
  }[]
  getRowValues: (runRow: RunRowLike) => Record<string, string>
}

export class MetricRecord extends MetricRecordCore {
  override preprocessMetadataBeforeSave(): MetricRecord {
    return this
  }

  /**
   * Returns explanation table definition and row value getter for the runs table (when metricIdFilter is set).
   * Takes the list of run rows so the metric can derive column count and titles from all metric outputs.
   * Override in subclasses; base returns no columns.
   */
  getExplanationTable(_runRows: RunRowLike[]): ExplanationTableResult {
    return { columns: [], getRowValues: () => ({}) }
  }

  /**
   * Get word-level highlights for transcript messages.
   * Returns null by default (not implemented for most metrics).
   * Override in subclasses to provide word-level highlighting.
   * @param mo The metric output
   * @param transcript The transcript messages
   * @returns Array of word highlights or null if not implemented
   */
  getHighlightedWords(
    _mo: RunMetricOutput,
    _transcript: Message[],
  ): WordHighlight[] | null {
    return null
  }

  /**
   * Render an annotation for a word highlight.
   * The metric inspects the highlight to determine if it should handle it
   * and how to render it. This allows each metric to define its own
   * annotation rendering logic without modifying AnnotatableMessage.
   * @param context The annotation render context with highlight data
   * @param key React key for the element
   * @returns React node to render, or null if this metric doesn't handle this highlight
   */
  renderAnnotation(
    _context: AnnotationRenderContext,
    _key: string,
  ): React.ReactNode | null {
    // Base class returns null (doesn't handle any annotations)
    return null
  }

  isMetricEnabled(
    _agent: AgentInfo,
    _simulatorType: SimulatorType,
  ): { result: boolean; error_message: string } {
    return { result: true, error_message: "" }
  }

  _renderStatusBasedBadge(
    errorMessage: string,
    errorStatus: string,
    rerunConfig: MetricsOnlyLaunchConfig,
    reRunMetric?: (launchConfig: MetricsOnlyLaunchConfig) => Promise<unknown>,
  ): React.ReactNode {
    return renderStatusBasedBadge(
      errorMessage,
      errorStatus,
      rerunConfig,
      reRunMetric,
    )
  }

  /**
   * Return a formatted display string for a metric output.
   * Subclasses can override for custom formatting (e.g. Pass/Fail, percentages).
   */
  getDisplayValueString(mo: MetricOutputDisplaySource): string | null {
    return getDefaultDisplayValueString(mo)
  }

  /**
   * Return a formatted display string for a human review value.
   * Subclasses can override for custom formatting.
   */
  getHumanReviewDisplayValue(review: {
    output_type: string
    float_value: number | null
    string_value: string | null
    set_value: string[] | null
    json_value: Record<string, unknown> | null
  }): string | null {
    return getDefaultHumanReviewDisplayValue(review)
  }

  //Can be overridden by child classes
  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return renderDefaultSimulationCard(
      mo,
      this.metric_metadata?.target_condition,
      this.unit,
      this,
    )
  }

  /**
   * Render an aggregate chip for a group of simulation outputs under a compare-by dimension.
   *
   * - dimension: which dimension we're comparing (agent/persona/test_case/metadata)
   * - metadataKey: the metadata key when dimension === "metadata"
   * - groupKey: the specific value within that dimension (e.g. agent_id, persona_id, test_case_id, or metadata value)
   * - metricOutputs: list of metric outputs in this group
   * - mode: aggregation mode (avg | median | p95 | min | max)
   */
  renderGroupedSimulationCard(
    options: GroupedSimulationCardOptions,
  ): React.ReactNode {
    return renderGroupedSimulationCard(options, {
      aggregateUnits: this.aggregate_units,
      unit: this.unit,
      strategies: {
        getNumericValues: (metricOutputs) =>
          this._getNumericValuesForAggregation(metricOutputs),
        getAverage: (values) => this._getAveragedValue(values),
        getMedian: (values) => this._getMedianValue(values),
        getP95: (values) => this._getP95Value(values),
        getMin: (values) => this._getMinValue(values),
        getMax: (values) => this._getMaxValue(values),
      },
    })
  }

  /**
   * Base helpers for numeric aggregation – subclasses can override any of these
   * to customize how grouped statistics are computed.
   */
  protected _getNumericValuesForAggregation(
    metricOutputs: RunMetricOutput[],
  ): number[] {
    return getNumericValuesForAggregation(metricOutputs)
  }

  protected _getAveragedValue(values: number[]): number | null {
    return getAverageValue(values)
  }

  protected _getMedianValue(values: number[]): number | null {
    return getMedianValue(values)
  }

  protected _getP95Value(values: number[]): number | null {
    return getP95Value(values)
  }

  protected _getMinValue(values: number[]): number | null {
    return getMinValue(values)
  }

  protected _getMaxValue(values: number[]): number | null {
    return getMaxValue(values)
  }

  renderBadge(
    mo: RunMetricOutput,
    reRunMetric?: (launchConfig: MetricsOnlyLaunchConfig) => Promise<unknown>,
  ): React.ReactNode {
    return renderDefaultBadge(
      this.metric_name,
      (metricOutput) => this._renderSimulationCard(metricOutput),
      mo,
      reRunMetric,
    )
  }

  //TO BE DEPRECATED
  renderValueOnlyTableCell(
    value: number | string,
    metricErrorStatus: string,
    _truncatedExplanation: string,
    errorMessage?: string,
    _organizationId?: string,
  ): React.ReactNode {
    return renderValueOnlyTableCellDefault(
      this.id || "",
      (metricOutput, rerunMetric) =>
        this.renderBadge(metricOutput, rerunMetric),
      value,
      metricErrorStatus,
      errorMessage,
    )
  }

  getSingleMetricOutputNode(_mo: RunMetricOutput): React.ReactNode | null {
    return null
  }

  // Get the header value string for display in card header
  getHeaderValue(_mo: RunMetricOutput[]): string | null {
    return null
  }

  // Value used to highlight this output in chart visualizations. Override when
  // float_value is stored in different units than the chart bars (e.g. composite).
  getHighlightValueForMetricOutput(
    mo: RunMetricOutput,
  ): number | string | null {
    return mo.float_value ?? mo.string_value ?? null
  }

  //How a list of values is viewed
  getOverviewNode(
    _mo: RunMetricOutput[],
    _options?: {
      highlightValue?: number | string | null
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode | null {
    return null
  }

  /** When compare-by is active (e.g. multi-run by agent/persona), return overview node that shows comparison. Uses runRows to derive this metric’s outputs and group by dimension. Default: null (card falls back to getOverviewNode). */
  getComparedOverviewNode(
    _runRows: RunRowLike[],
    _compareBy: CompareBy,
  ): React.ReactNode | null {
    return null
  }

  /** Extract this metric’s completed outputs from run rows. Used by getComparedOverviewNode. */
  protected _getMetricOutputsFromRunRows(
    runRows: RunRowLike[],
  ): RunMetricOutput[] {
    return getCompletedMetricOutputs(runRows, this.id)
  }

  _renderDeepDive(
    _mo: RunMetricOutput,
    _testCase: BaseTestCaseRecord,
    _dispatchMessageSelect?: (messageIndex: number | null) => void,
    _context?: DeepDiveContext,
  ): React.ReactNode {
    return null
  }

  /**
   * Returns structured sections for the deep dive expanded view.
   * Each section becomes a column in the grid, with Notes always appended as the final column.
   * Max 3 sections (+ notes = 4 columns). Returns null to fall back to _renderDeepDive.
   */
  getDeepDiveSections(
    _mo: RunMetricOutput,
    _testCase: BaseTestCaseRecord,
  ): { label: string; content: React.ReactNode }[] | null {
    return null
  }

  /**
   * Returns the metric's CURRENT question, rendered for display in Human Review.
   * Base returns the raw string. Binary LLM overrides to substitute
   * `{{expected_output.foo}}` placeholders against the test case. Returns null
   * when there is no question configured (e.g. non-LLM metrics).
   *
   * This is intentionally separate from `getDeepDiveSections`: the deep dive
   * shows the question the RUN used (diagnostic), while HR grades against the
   * current criteria. Do NOT wire this into `getDeepDiveSections`.
   */
  renderCurrentQuestion(
    _testCase: BaseTestCaseRecord,
    callContext?: AttributeContext,
  ): React.ReactNode | null {
    const question = this.metric_metadata?.question
    if (!question) {
      return null
    }
    // Human Review supplies the call context so `{{prefix.key}}` template
    // variables are substituted (and highlighted) from the actual call.
    return callContext
      ? renderQuestionWithAttributes(question, callContext)
      : question
  }

  renderDeepDive(
    mo: RunMetricOutput,
    testCase: BaseTestCaseRecord,
    dispatchMessageSelect?: (messageIndex: number | null) => void,
    context?: DeepDiveContext,
  ): React.ReactNode {
    return renderDeepDiveDefault(
      (metricOutput, metricTestCase, messageIndex, deepDiveContext) =>
        this._renderDeepDive(
          metricOutput,
          metricTestCase,
          messageIndex,
          deepDiveContext,
        ),
      mo,
      testCase,
      dispatchMessageSelect,
      context,
    )
  }

  hasCustomVotingUI = false

  /**
   * When true, MetricGrid falls back to HumanFeedbackWrapper (voting buttons)
   * if renderDeepDiveAnnotationsPanel returns null. Set to false for metrics
   * that provide their own annotations panel but may return null when data is
   * unavailable — in those cases a null human review node is preferable.
   */
  hasHumanReviewFallback = true

  /**
   * When false, the card has no expandable detail region: the metric renders
   * everything it has inside the card itself, so hosts omit the expand control
   * and never mount the expanded detail.
   */
  hasDeepDiveDetail = true

  /**
   * True when the metric places the host's reviewer-notes node itself — in
   * `renderDeepDiveAnnotationsPanel` or `renderHumanReviewDetail` — so hosts
   * that also have a notes slot of their own leave it empty rather than
   * mounting a second editor for the same annotation.
   */
  ownsHumanReviewNotes = false

  /**
   * True when the metric's own card layout already states this output's
   * result — a totals row in its review table, say — so the card omits the
   * result badge under the title rather than printing the same figure twice.
   * Takes the output because a layout that states a structured result may
   * have nothing to say about a scalar or legacy one.
   */
  statesResultInCardDetail(_mo: RunMetricOutput): boolean {
    return false
  }

  /**
   * Optional override for metrics with custom voting UI. `notesNode` is the
   * pre-built <HumanFeedbackNotesWrapper> supplied by the host so the metric
   * can place reviewer notes inside its own layout; metrics that ignore it
   * leave notes to the host's default placement.
   */
  renderDeepDiveAnnotationsPanel(
    _simulationOutputId: string,
    _annotationsForMetric: Annotation[],
    _metricOutput?: RunMetricOutput,
    _blindLabeling?: boolean,
    _projectId?: string,
    _notesNode?: React.ReactNode,
    _projectBlindLabeling?: boolean,
  ): React.ReactNode {
    return null
  }

  /**
   * Optional full-width companion to `renderDeepDiveAnnotationsPanel`. That
   * panel shares the card's top row with the AI result, so a metric whose
   * review UI is itself a wide layout — a per-criterion table, say — keeps
   * only its score header up there and returns the wide part here, where the
   * card renders it across its full width. `notesNode` is the host's
   * <HumanFeedbackNotesWrapper>, for metrics that place notes themselves.
   * `transcriptContext` is the conversation the metric was computed against,
   * for a review UI that points the reviewer back at the messages it cites.
   */
  renderHumanReviewDetail(
    _simulationOutputId: string,
    _annotationsForMetric: Annotation[],
    _metricOutput?: RunMetricOutput,
    _blindLabeling?: boolean,
    _projectId?: string,
    _notesNode?: React.ReactNode,
    _transcriptContext?: MetricReviewTranscriptContext,
  ): React.ReactNode {
    return null
  }

  /**
   * Optional override for metrics that need full control over the expanded
   * detail panel layout. When non-null, MetricGrid renders this instead of
   * the default column grid + notes column. The `notesNode` argument is the
   * pre-built <HumanFeedbackNotesWrapper> so the metric can place it wherever
   * it wants inside the panel.
   */
  renderDeepDivePanel(
    _notesNode: React.ReactNode,
    _metricOutput: RunMetricOutput | undefined,
    _onMessageSelect?: (messageIndex: number | null) => void,
    _context?: {
      simulationOutputId?: string
      annotationsForMetric?: Annotation[]
    },
  ): React.ReactNode {
    return null
  }

  //If the metric should be displayed on the audio, give information as to how
  getAudioRegions(
    _mo: MetricOutputDisplaySource | undefined,
    _options?: { theme?: "light" | "dark" },
  ): AudioRegionParams[] {
    return []
  }

  // Per-span region color for an LLM verdict; undefined => default highlight.
  getAudioRegionColorForVerdict(_verdict: string | null): string | undefined {
    return undefined
  }

  // Return a config to opt this metric into audio region human review.
  // Metrics that support editable region review override this.
  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return null
  }

  // Return a config to opt this metric into per-message human review.
  // Metrics that need per-message annotation in the transcript override this.
  getPerMessageReviewConfig(): PerMessageReviewConfig | null {
    return null
  }

  // Return a config to opt this metric into per-segment human review.
  // Metrics that need per-segment annotation on speaking segments override this.
  getPerSegmentReviewConfig(): PerSegmentReviewConfig | null {
    return null
  }

  // True if this metric overrides getAudioRegions (i.e. it produces audio region segments).
  get supportsAudioRegions(): boolean {
    return this.getAudioRegions !== MetricRecord.prototype.getAudioRegions
  }

  // True if metric studio should use segment-based review views (timeline, segment comparison).
  // Defaults to supportsAudioRegions, but binary audio metrics override to false
  // since they should show binary label tables instead of segment timelines.
  get supportsAudioSegmentReview(): boolean {
    return this.supportsAudioRegions
  }

  renderHumanFeedbackValueButton(
    _annotation: Annotation | null,
    _onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    _onExplanationChange: (explanation: string | null) => void,
    _otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    return null
  }

  // True if this metric renders a human-review annotation input in the deep
  // dive / assignments card — i.e. it either uses a custom voting UI panel or
  // overrides renderHumanFeedbackValueButton. Used to decide which un-run
  // metrics can be surfaced for annotation.
  get supportsHumanReview(): boolean {
    return (
      this.hasCustomVotingUI ||
      this.renderHumanFeedbackValueButton !==
        MetricRecord.prototype.renderHumanFeedbackValueButton
    )
  }

  renderAssignmentsTableAIResultCell(
    simulationOutputRow: RunRowLike,
    metricId: string,
  ): React.ReactNode {
    return renderAssignmentsTableAIResultCellDefault(
      this,
      simulationOutputRow,
      metricId,
    )
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    return getDefaultKeyboardShortcutHints()
  }

  renderAssignmentsTableReviewCell(
    _simulationOutputId: string,
    _annotations_for_metric: Annotation[],
    _projectId?: string,
    _metricOutput?: RunMetricOutput,
  ): React.ReactNode {
    return renderDefaultAssignmentsTableReviewCell()
  }

  /**
   * Return additional sub-rows to render below the main metric row in the
   * assignments table.  Each element should be a `<tr>`.  Return `null` when
   * no sub-rows are needed (the default).
   */
  renderAssignmentsTableSubRows(
    _simulationOutputId: string,
    _annotationsForMetric: Annotation[],
    _projectId?: string,
    _metricOutput?: RunMetricOutput,
  ): React.ReactNode {
    return renderDefaultAssignmentsTableSubRows()
  }

  // Default filter selection renderer
  renderFilterSelection(
    value: string,
    onValueChange: (value: string) => void,
  ): React.ReactNode {
    return renderDefaultFilterSelection(value, onValueChange)
  }

  // Render form input for alert rule configuration
  renderSingleOutputAlertForm(
    value: string,
    onValueChange: (value: string) => void,
  ): React.ReactNode {
    return renderDefaultSingleOutputAlertForm(value, onValueChange)
  }

  // Render form input for aggregate alert rule configuration
  renderAggregateAlertForm(
    threshold: string,
    onThresholdChange: (value: string) => void,
    matchValue?: string,
    onMatchValueChange?: (value: string) => void,
  ): React.ReactNode {
    return renderDefaultAggregateAlertForm(
      this.output_type,
      threshold,
      onThresholdChange,
      matchValue,
      onMatchValueChange,
    )
  }

  renderEditorMetadata(
    _metricRecordData: MetricRecordData,
    _onChange: (metric: MetricRecordData) => void,
    _textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    _editorContext?: MetricEditorContext,
  ): React.ReactNode {
    return renderEmptyEditorMetadata(_metricRecordData, _onChange, _textAreaRef)
  }

  renderReadOnlyMetadata(metricRecordData: MetricRecordData): React.ReactNode {
    return renderDefaultReadOnlyMetadata(metricRecordData)
  }

  renderMetadataField(_metricRecordData: MetricRecordData): React.ReactNode {
    return renderEmptyMetadataField(_metricRecordData)
  }

  renderMetricType(): React.ReactNode {
    return renderEmptyMetricType()
  }

  async evaluateTranscript(
    _simulationWithContext: SimulationWithContext,
    _getCompletionMutation: unknown,
  ): Promise<TestResult> {
    return evaluateTranscriptDefault()
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    _context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: ChartBarFilter) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    return renderInDepthRunReviewDefault(this, metricOutputs, _context)
  }

  /**
   * Render in-depth view for multi-run pages when compare-by is active.
   * Shows comparison charts grouped by the compare-by dimension (agent, persona, test case, or metadata).
   * Override in subclasses to customize comparison visualization.
   */
  renderComparedInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode {
    return renderComparedInDepthRunReviewDefault(
      this,
      metricOutputs,
      runRows,
      compareBy,
    )
  }
}
